/**
 * EmploymentRoleDetailModal — View employment role details + report type rates
 * (with nested hospital bonuses) + allowances (inline CRUD).
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Trash2,
  Hash,
  Type,
  FileText,
  CheckCircle,
  Plus,
  X,
  Loader2,
  Calendar,
  Clock,
  DollarSign,
  ListChecks,
  BadgeDollarSign,
  Pencil,
  ChevronDown,
  ChevronUp,
  Building2,
} from 'lucide-react';
import { FormModal, MODAL_COLORS, useModal } from '@/components/modal';
import { localizedName } from '@/utils/localized';
import {
  useEmploymentRoleDetail,
  useDeleteEmploymentRole,
  useEmploymentRoleRates,
  useCreateRate,
  useUpdateRate,
  useDeleteRate,
  useCreateHospitalBonus,
  useUpdateHospitalBonus,
  useDeleteHospitalBonus,
  useCreateSupervisionBonus,
  useUpdateSupervisionBonus,
  useDeleteSupervisionBonus,
  useActiveEmploymentRoles,
  useEmploymentRoleAllowances,
  useCreateEmploymentRoleAllowance,
  useUpdateEmploymentRoleAllowance,
  useDeleteEmploymentRoleAllowance,
  useActiveAllowanceTypes,
} from '../hooks';
import { useHospitals } from '@/features/hospitals';
import { triggerHapticFeedback } from '../utils/adminAnimations';
import toast from 'react-hot-toast';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { http } from '@/network/http';
import { useQuery } from '@tanstack/react-query';
import type { EmploymentRole, EmploymentRoleRate, EmploymentRoleHospitalBonus, EmploymentRoleAllowance, SupervisionBonusConfig } from '../types';

const cardStyle = {
  backgroundColor: MODAL_COLORS.interactive,
  border: `1px solid ${MODAL_COLORS.border}`,
};

const actionBtnStyle = {
  border: `1px solid ${MODAL_COLORS.border}`,
};

const PERIODS = ['monthly', 'per_report', 'per_day', 'one_time', 'annual'] as const;

interface EmploymentRoleDetailModalProps {
  employmentRole: EmploymentRole;
  onClose: () => void;
  onEdit?: (role: EmploymentRole) => void;
}

export function EmploymentRoleDetailModal({ employmentRole, onClose, onEdit }: EmploymentRoleDetailModalProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const sym = useCurrencySymbol();
  const { openConfirm } = useModal();
  const { data: detail } = useEmploymentRoleDetail(employmentRole.id);
  const deleteMutation = useDeleteEmploymentRole();

  // --- Rates hooks ---
  const { data: rates, isLoading: isLoadingRates } = useEmploymentRoleRates(employmentRole.id);
  const createRateMutation = useCreateRate();
  const updateRateMutation = useUpdateRate();
  const deleteRateMutation = useDeleteRate();

  // --- Hospital bonus hooks ---
  const createBonusMutation = useCreateHospitalBonus();
  const updateBonusMutation = useUpdateHospitalBonus();
  const deleteBonusMutation = useDeleteHospitalBonus();

  // --- Supervision bonus hooks ---
  const createSupBonusMutation = useCreateSupervisionBonus();
  const updateSupBonusMutation = useUpdateSupervisionBonus();
  const deleteSupBonusMutation = useDeleteSupervisionBonus();

  // --- Active employment roles for source role selection ---
  const { data: activeEmploymentRoles } = useActiveEmploymentRoles();

  // --- Hospitals for bonus dropdown ---
  const { data: hospitalsData } = useHospitals({ isActive: 'true', limit: 100 });
  const hospitals = hospitalsData?.hospitals || [];

  // --- Allowances hooks ---
  const { data: allowances, isLoading: isLoadingAllowances } = useEmploymentRoleAllowances(employmentRole.id);
  const createAllowanceMutation = useCreateEmploymentRoleAllowance();
  const updateAllowanceMutation = useUpdateEmploymentRoleAllowance();
  const deleteAllowanceMutation = useDeleteEmploymentRoleAllowance();
  const { data: allowanceTypes } = useActiveAllowanceTypes();

  // --- Rate state ---
  const [showAddRate, setShowAddRate] = useState(false);
  const [editingRate, setEditingRate] = useState<EmploymentRoleRate | null>(null);
  const [rateReportTypeId, setRateReportTypeId] = useState('');
  const [ratePerUnit, setRatePerUnit] = useState('');
  const [expandedRateIds, setExpandedRateIds] = useState<Set<string>>(new Set());

  // --- Hospital bonus state ---
  const [addingBonusForRateId, setAddingBonusForRateId] = useState<string | null>(null);
  const [bonusHospitalId, setBonusHospitalId] = useState('');
  const [bonusAmount, setBonusAmount] = useState('');
  const [editingBonus, setEditingBonus] = useState<EmploymentRoleHospitalBonus | null>(null);
  const [editingBonusAmount, setEditingBonusAmount] = useState('');

  // --- Supervision bonus state ---
  const [addingSupBonusForRateId, setAddingSupBonusForRateId] = useState<string | null>(null);
  const [supBonusHospitalId, setSupBonusHospitalId] = useState('');
  const [supBonusAmount, setSupBonusAmount] = useState('');
  const [supBonusSourceRoleIds, setSupBonusSourceRoleIds] = useState<string[]>([]);
  const [editingSupBonus, setEditingSupBonus] = useState<SupervisionBonusConfig | null>(null);
  const [editingSupBonusAmount, setEditingSupBonusAmount] = useState('');
  const [editingSupBonusSourceRoleIds, setEditingSupBonusSourceRoleIds] = useState<string[]>([]);

  // --- Allowance state ---
  const [showAddAllowance, setShowAddAllowance] = useState(false);
  const [editingAllowance, setEditingAllowance] = useState<EmploymentRoleAllowance | null>(null);
  const [allowanceTypeId, setAllowanceTypeId] = useState('');
  const [allowanceAmount, setAllowanceAmount] = useState('');
  const [allowancePeriod, setAllowancePeriod] = useState('monthly');
  const [allowanceNotes, setAllowanceNotes] = useState('');

  const [isOpen, setIsOpen] = useState(true);
  const requestClose = useCallback(() => setIsOpen(false), []);

  // Fetch all report types for the rate dropdown
  const { data: allReportTypes } = useQuery({
    queryKey: ['report-types-for-assignment'],
    queryFn: async () => {
      const { data } = await http.get<{ success: boolean; reportTypes: Array<{ id: string; code: string; name: string; nameEn: string | null; unit: string }> }>('/report-types', {
        params: { limit: 100, isActive: 'true' },
      });
      return data.reportTypes;
    },
    enabled: showAddRate,
  });

  const role = detail || employmentRole;

  // --- Period label helper ---
  const periodLabel = (period: string) => {
    const key = `employmentRoles.period${period.charAt(0).toUpperCase()}${period.slice(1).replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())}`;
    return t(key, period);
  };

  // --- Base rate period label ---
  const baseRatePeriodLabel = (period: string) => {
    switch (period) {
      case 'monthly': return t('employmentRoles.monthly');
      case 'hourly': return t('employmentRoles.hourly');
      case 'daily': return t('employmentRoles.daily');
      default: return period;
    }
  };

  // ===== Rate handlers =====
  const handleAddRate = async () => {
    if (!rateReportTypeId || !ratePerUnit) return;
    try {
      triggerHapticFeedback();
      await createRateMutation.mutateAsync({
        id: employmentRole.id,
        data: {
          reportTypeId: rateReportTypeId,
          ratePerUnit: parseFloat(ratePerUnit) || 0,
        },
      });
      toast.success(t('employmentRoles.rates.createSuccess'));
      setRateReportTypeId('');
      setRatePerUnit('');
      setShowAddRate(false);
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleUpdateRate = async () => {
    if (!editingRate || !ratePerUnit) return;
    try {
      triggerHapticFeedback();
      await updateRateMutation.mutateAsync({
        roleId: employmentRole.id,
        rateId: editingRate.id,
        data: { ratePerUnit: parseFloat(ratePerUnit) || 0 },
      });
      toast.success(t('employmentRoles.rates.updateSuccess'));
      setEditingRate(null);
      setRatePerUnit('');
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDeleteRate = async (rateId: string) => {
    try {
      triggerHapticFeedback();
      await deleteRateMutation.mutateAsync({ id: employmentRole.id, rateId });
      toast.success(t('employmentRoles.rates.deleteSuccess'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const startEditRate = (rate: EmploymentRoleRate) => {
    setEditingRate(rate);
    setRatePerUnit(String(rate.ratePerUnit));
    setShowAddRate(false);
  };

  const cancelEditRate = () => {
    setEditingRate(null);
    setRatePerUnit('');
  };

  const toggleRateExpanded = (rateId: string) => {
    setExpandedRateIds((prev) => {
      const next = new Set(prev);
      if (next.has(rateId)) {
        next.delete(rateId);
      } else {
        next.add(rateId);
      }
      return next;
    });
  };

  // ===== Hospital bonus handlers =====
  const handleAddBonus = async (rateId: string) => {
    if (!bonusHospitalId || !bonusAmount) return;
    try {
      triggerHapticFeedback();
      await createBonusMutation.mutateAsync({
        roleId: employmentRole.id,
        rateId,
        data: {
          hospitalId: bonusHospitalId,
          bonusAmount: parseFloat(bonusAmount) || 0,
        },
      });
      toast.success(t('employmentRoles.hospitalBonuses.createSuccess'));
      setBonusHospitalId('');
      setBonusAmount('');
      setAddingBonusForRateId(null);
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleUpdateBonus = async (rateId: string) => {
    if (!editingBonus || !editingBonusAmount) return;
    try {
      triggerHapticFeedback();
      await updateBonusMutation.mutateAsync({
        roleId: employmentRole.id,
        rateId,
        bonusId: editingBonus.id,
        data: { bonusAmount: parseFloat(editingBonusAmount) || 0 },
      });
      toast.success(t('employmentRoles.hospitalBonuses.updateSuccess'));
      setEditingBonus(null);
      setEditingBonusAmount('');
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDeleteBonus = async (rateId: string, bonusId: string) => {
    try {
      triggerHapticFeedback();
      await deleteBonusMutation.mutateAsync({ roleId: employmentRole.id, rateId, bonusId });
      toast.success(t('employmentRoles.hospitalBonuses.deleteSuccess'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const startEditBonus = (bonus: EmploymentRoleHospitalBonus) => {
    setEditingBonus(bonus);
    setEditingBonusAmount(String(bonus.bonusAmount));
    setAddingBonusForRateId(null);
  };

  const cancelEditBonus = () => {
    setEditingBonus(null);
    setEditingBonusAmount('');
  };

  // ===== Supervision bonus handlers =====
  const handleAddSupBonus = async (rateId: string) => {
    if (!supBonusHospitalId || !supBonusAmount || supBonusSourceRoleIds.length === 0) return;
    try {
      triggerHapticFeedback();
      await createSupBonusMutation.mutateAsync({
        roleId: employmentRole.id,
        rateId,
        data: {
          hospitalId: supBonusHospitalId,
          bonusPerUnit: parseFloat(supBonusAmount) || 0,
          sourceEmploymentRoleIds: supBonusSourceRoleIds,
        },
      });
      toast.success(t('employmentRoles.supervisionBonuses.createSuccess'));
      setSupBonusHospitalId('');
      setSupBonusAmount('');
      setSupBonusSourceRoleIds([]);
      setAddingSupBonusForRateId(null);
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleUpdateSupBonus = async (rateId: string) => {
    if (!editingSupBonus || !editingSupBonusAmount) return;
    try {
      triggerHapticFeedback();
      await updateSupBonusMutation.mutateAsync({
        roleId: employmentRole.id,
        rateId,
        bonusId: editingSupBonus.id,
        data: {
          bonusPerUnit: parseFloat(editingSupBonusAmount) || 0,
          sourceEmploymentRoleIds: editingSupBonusSourceRoleIds,
        },
      });
      toast.success(t('employmentRoles.supervisionBonuses.updateSuccess'));
      setEditingSupBonus(null);
      setEditingSupBonusAmount('');
      setEditingSupBonusSourceRoleIds([]);
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDeleteSupBonus = async (rateId: string, bonusId: string) => {
    try {
      triggerHapticFeedback();
      await deleteSupBonusMutation.mutateAsync({ roleId: employmentRole.id, rateId, bonusId });
      toast.success(t('employmentRoles.supervisionBonuses.deleteSuccess'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const startEditSupBonus = (bonus: SupervisionBonusConfig) => {
    setEditingSupBonus(bonus);
    setEditingSupBonusAmount(String(bonus.bonusPerUnit));
    setEditingSupBonusSourceRoleIds((bonus.sourceRoles || []).map(sr => sr.sourceEmploymentRole?.id).filter(Boolean) as string[]);
    setAddingSupBonusForRateId(null);
  };

  const cancelEditSupBonus = () => {
    setEditingSupBonus(null);
    setEditingSupBonusAmount('');
    setEditingSupBonusSourceRoleIds([]);
  };

  const toggleSupBonusSourceRole = (roleId: string, editing: boolean) => {
    if (editing) {
      setEditingSupBonusSourceRoleIds(prev =>
        prev.includes(roleId) ? prev.filter(id => id !== roleId) : [...prev, roleId],
      );
    } else {
      setSupBonusSourceRoleIds(prev =>
        prev.includes(roleId) ? prev.filter(id => id !== roleId) : [...prev, roleId],
      );
    }
  };

  // ===== Allowance handlers =====
  const resetAllowanceForm = () => {
    setAllowanceTypeId('');
    setAllowanceAmount('');
    setAllowancePeriod('monthly');
    setAllowanceNotes('');
  };

  const handleAddAllowance = async () => {
    if (!allowanceTypeId || !allowanceAmount) return;
    try {
      triggerHapticFeedback();
      await createAllowanceMutation.mutateAsync({
        employmentRoleId: employmentRole.id,
        data: {
          allowanceTypeId,
          amount: parseFloat(allowanceAmount) || 0,
          period: allowancePeriod,
          notes: allowanceNotes.trim() || undefined,
        },
      });
      toast.success(t('employmentRoles.allowanceCreateSuccess'));
      resetAllowanceForm();
      setShowAddAllowance(false);
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleUpdateAllowance = async () => {
    if (!editingAllowance || !allowanceAmount) return;
    try {
      triggerHapticFeedback();
      await updateAllowanceMutation.mutateAsync({
        employmentRoleId: employmentRole.id,
        allowanceId: editingAllowance.id,
        data: {
          amount: parseFloat(allowanceAmount) || 0,
          period: allowancePeriod,
          notes: allowanceNotes.trim() || undefined,
        },
      });
      toast.success(t('employmentRoles.allowanceUpdateSuccess'));
      setEditingAllowance(null);
      resetAllowanceForm();
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDeleteAllowance = async (allowanceId: string) => {
    try {
      triggerHapticFeedback();
      await deleteAllowanceMutation.mutateAsync({ employmentRoleId: employmentRole.id, allowanceId });
      toast.success(t('employmentRoles.allowanceDeleteSuccess'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const startEditAllowance = (a: EmploymentRoleAllowance) => {
    setEditingAllowance(a);
    setAllowanceAmount(String(a.amount));
    setAllowancePeriod(a.period);
    setAllowanceNotes(a.notes || '');
    setShowAddAllowance(false);
  };

  const cancelEditAllowance = () => {
    setEditingAllowance(null);
    resetAllowanceForm();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={requestClose}
      onExitComplete={onClose}
      title={localizedName(role, lang)}
      maxWidth="max-w-lg"
    >
      <div className="space-y-2">
        {/* Status */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 opacity-60" />
            <span className="text-sm font-bold">{t('employmentRoles.status', 'Status')}</span>
            <span className="text-sm font-semibold opacity-70">
              {role.isActive ? t('employmentRoles.active') : t('employmentRoles.inactive')}
            </span>
          </div>
        </div>

        {/* Details */}
        <div className="rounded-xl p-3 space-y-1.5" style={cardStyle}>
          <div className="flex items-center gap-2 text-sm">
            <Hash className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('employmentRoles.code')}:</span>
            <span className="opacity-70">{role.code}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Type className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('employmentRoles.name')}:</span>
            <span className="opacity-70">{role.name}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Type className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('employmentRoles.nameEn')}:</span>
            <span className="opacity-70">{role.nameEn || '\u2014'}</span>
          </div>
          {role.description && (
            <div className="flex items-start gap-2 text-sm">
              <FileText className="w-3.5 h-3.5 opacity-50 mt-0.5" />
              <span className="font-bold">{t('employmentRoles.description')}:</span>
              <span className="opacity-70">{role.description}</span>
            </div>
          )}
        </div>

        {/* Base Salary Info */}
        <div className="rounded-xl p-3 space-y-1.5" style={cardStyle}>
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('employmentRoles.hasBaseSalary')}:</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${role.hasBaseSalary ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'}`}>
              {role.hasBaseSalary ? t('common.yes') : t('common.no')}
            </span>
          </div>
          {role.hasBaseSalary && (
            <>
              <div className="flex items-center gap-2 text-sm">
                <DollarSign className="w-3.5 h-3.5 opacity-50" />
                <span className="font-bold">{t('employmentRoles.baseRate')}:</span>
                <span className="opacity-70">{role.baseRate}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-3.5 h-3.5 opacity-50" />
                <span className="font-bold">{t('employmentRoles.baseRatePeriod')}:</span>
                <span className="opacity-70">{baseRatePeriodLabel(role.baseRatePeriod)}</span>
              </div>
            </>
          )}
        </div>

        {/* Report Type Rates */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ListChecks className="w-4 h-4 opacity-60" />
              <span className="text-sm font-bold">{t('employmentRoles.rates.title')}</span>
            </div>
            <button
              onClick={() => {
                triggerHapticFeedback();
                setShowAddRate(!showAddRate);
                cancelEditRate();
              }}
              className="flex items-center gap-1.5 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors cursor-pointer"
              style={actionBtnStyle}
            >
              {showAddRate ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
              {showAddRate ? t('common.cancel') : t('employmentRoles.rates.add')}
            </button>
          </div>

          {/* Add Rate Form */}
          {showAddRate && (
            <div className="flex gap-2 mb-2 p-2 rounded-lg" style={{ backgroundColor: MODAL_COLORS.surface }}>
              <select
                value={rateReportTypeId}
                onChange={(e) => setRateReportTypeId(e.target.value)}
                className="flex-1 p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
              >
                <option value="">{t('employmentRoles.selectReportType')}</option>
                {(allReportTypes || []).map((rt) => (
                  <option key={rt.id} value={rt.id}>
                    {localizedName(rt, lang)}
                  </option>
                ))}
              </select>
              <input
                type="number"
                value={ratePerUnit}
                onChange={(e) => setRatePerUnit(e.target.value)}
                min={0}
                step={0.01}
                placeholder={t('employmentRoles.rates.ratePerUnit')}
                className="w-24 p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
              />
              <button
                onClick={handleAddRate}
                disabled={!rateReportTypeId || !ratePerUnit || createRateMutation.isPending}
                className="px-2.5 py-1.5 rounded-lg text-xs font-bold cursor-pointer disabled:opacity-50 text-white"
                style={{ backgroundColor: 'var(--color-primary-500)', ...actionBtnStyle }}
              >
                {createRateMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('common.add')}
              </button>
            </div>
          )}

          {/* Edit Rate Form */}
          {editingRate && (
            <div className="flex gap-2 mb-2 p-2 rounded-lg" style={{ backgroundColor: 'rgba(254, 249, 195, 0.5)' }}>
              <div
                className="flex-1 p-1.5 rounded-lg text-sm font-semibold"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: MODAL_COLORS.surface }}
              >
                {editingRate.reportType ? localizedName(editingRate.reportType, lang) : editingRate.reportTypeId}
              </div>
              <input
                type="number"
                value={ratePerUnit}
                onChange={(e) => setRatePerUnit(e.target.value)}
                min={0}
                step={0.01}
                placeholder={t('employmentRoles.rates.ratePerUnit')}
                className="w-24 p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
              />
              <button
                onClick={handleUpdateRate}
                disabled={!ratePerUnit || updateRateMutation.isPending}
                className="px-2.5 py-1.5 rounded-lg text-xs font-bold cursor-pointer disabled:opacity-50 text-white"
                style={{ backgroundColor: 'var(--color-primary-500)', ...actionBtnStyle }}
              >
                {updateRateMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('common.save')}
              </button>
              <button
                onClick={cancelEditRate}
                className="px-2 py-1.5 rounded-lg text-xs font-bold cursor-pointer glass-surface"
                style={actionBtnStyle}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Rates List */}
          {isLoadingRates ? (
            <div className="flex justify-center py-3">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : (rates || []).length === 0 ? (
            <p className="text-xs opacity-50 text-center py-2">{t('employmentRoles.rates.noResults')}</p>
          ) : (
            <div className="space-y-1">
              {(rates || []).map((rate) => {
                const isExpanded = expandedRateIds.has(rate.id);
                const bonuses = rate.hospitalBonuses || [];
                return (
                  <div key={rate.id}>
                    {/* Rate row */}
                    <div
                      className="flex items-center justify-between py-1 px-2 rounded-lg"
                      style={{ backgroundColor: MODAL_COLORS.surface }}
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <ListChecks className="w-3 h-3 opacity-40 flex-shrink-0" />
                        <span className="text-xs font-semibold truncate">
                          {rate.reportType ? localizedName(rate.reportType, lang) : rate.reportTypeId}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs font-medium opacity-70">
                          {rate.ratePerUnit} {sym} / {rate.reportType?.unit ? t(`reportTypesAdmin.unit_${rate.reportType.unit}`, rate.reportType.unit) : t('reportTypesAdmin.unit')}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRateExpanded(rate.id);
                          }}
                          className="p-1 rounded hover:bg-neutral-100 cursor-pointer"
                          title={t('employmentRoles.hospitalBonuses.title')}
                        >
                          {isExpanded ? <ChevronUp className="w-3 h-3 opacity-60" /> : <ChevronDown className="w-3 h-3 opacity-60" />}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditRate(rate);
                          }}
                          className="p-1 rounded hover:bg-blue-100 cursor-pointer"
                        >
                          <Pencil className="w-3 h-3 text-blue-500" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRate(rate.id);
                          }}
                          disabled={deleteRateMutation.isPending}
                          className="p-1 rounded hover:bg-red-100 cursor-pointer disabled:opacity-50"
                        >
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </button>
                      </div>
                    </div>

                    {/* Expanded: Hospital Bonuses */}
                    {isExpanded && (
                      <div className="ms-4 mt-1 mb-1 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold opacity-50 uppercase tracking-wide">
                            {t('employmentRoles.hospitalBonuses.title')}
                          </span>
                          <button
                            onClick={() => {
                              triggerHapticFeedback();
                              if (addingBonusForRateId === rate.id) {
                                setAddingBonusForRateId(null);
                                setBonusHospitalId('');
                                setBonusAmount('');
                              } else {
                                setAddingBonusForRateId(rate.id);
                                cancelEditBonus();
                              }
                            }}
                            className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold hover:bg-white/10 transition-colors cursor-pointer"
                            style={actionBtnStyle}
                          >
                            {addingBonusForRateId === rate.id ? <X className="w-2.5 h-2.5" /> : <Plus className="w-2.5 h-2.5" />}
                            {addingBonusForRateId === rate.id ? t('common.cancel') : t('employmentRoles.hospitalBonuses.add')}
                          </button>
                        </div>

                        {/* Add bonus form */}
                        {addingBonusForRateId === rate.id && (
                          <div className="flex gap-1.5 p-1.5 rounded-lg" style={{ backgroundColor: MODAL_COLORS.surface }}>
                            <select
                              value={bonusHospitalId}
                              onChange={(e) => setBonusHospitalId(e.target.value)}
                              className="flex-1 p-1 rounded text-xs font-semibold outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
                              style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
                            >
                              <option value="">{t('employmentRoles.hospitalBonuses.selectHospital')}</option>
                              {hospitals.map((h) => (
                                <option key={h.id} value={h.id}>
                                  {localizedName(h, lang)}
                                </option>
                              ))}
                            </select>
                            <input
                              type="number"
                              value={bonusAmount}
                              onChange={(e) => setBonusAmount(e.target.value)}
                              min={0}
                              step={0.01}
                              placeholder={t('employmentRoles.hospitalBonuses.bonusAmount')}
                              className="w-20 p-1 rounded text-xs font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                              style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
                            />
                            <button
                              onClick={() => handleAddBonus(rate.id)}
                              disabled={!bonusHospitalId || !bonusAmount || createBonusMutation.isPending}
                              className="px-2 py-1 rounded text-[10px] font-bold cursor-pointer disabled:opacity-50 text-white"
                              style={{ backgroundColor: 'var(--color-primary-500)', ...actionBtnStyle }}
                            >
                              {createBonusMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : t('common.add')}
                            </button>
                          </div>
                        )}

                        {/* Edit bonus form */}
                        {editingBonus && bonuses.some((b) => b.id === editingBonus.id) && (
                          <div className="flex gap-1.5 p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(254, 249, 195, 0.5)' }}>
                            <div
                              className="flex-1 p-1 rounded text-xs font-semibold"
                              style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: MODAL_COLORS.surface }}
                            >
                              {editingBonus.hospital ? localizedName(editingBonus.hospital, lang) : editingBonus.hospitalId}
                            </div>
                            <input
                              type="number"
                              value={editingBonusAmount}
                              onChange={(e) => setEditingBonusAmount(e.target.value)}
                              min={0}
                              step={0.01}
                              placeholder={t('employmentRoles.hospitalBonuses.bonusAmount')}
                              className="w-20 p-1 rounded text-xs font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                              style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
                            />
                            <button
                              onClick={() => handleUpdateBonus(rate.id)}
                              disabled={!editingBonusAmount || updateBonusMutation.isPending}
                              className="px-2 py-1 rounded text-[10px] font-bold cursor-pointer disabled:opacity-50 text-white"
                              style={{ backgroundColor: 'var(--color-primary-500)', ...actionBtnStyle }}
                            >
                              {updateBonusMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : t('common.save')}
                            </button>
                            <button
                              onClick={cancelEditBonus}
                              className="px-1.5 py-1 rounded text-[10px] font-bold cursor-pointer glass-surface"
                              style={actionBtnStyle}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}

                        {/* Bonuses list */}
                        {bonuses.length === 0 ? (
                          <p className="text-[10px] opacity-40 text-center py-1">{t('employmentRoles.hospitalBonuses.noResults')}</p>
                        ) : (
                          bonuses.map((bonus) => (
                            <div
                              key={bonus.id}
                              className="flex items-center justify-between py-0.5 px-1.5 rounded"
                              style={{ backgroundColor: MODAL_COLORS.surface }}
                            >
                              <div className="flex items-center gap-1 min-w-0">
                                <Building2 className="w-2.5 h-2.5 opacity-40 flex-shrink-0" />
                                <span className="text-[11px] font-semibold truncate">
                                  {bonus.hospital ? localizedName(bonus.hospital, lang) : bonus.hospitalId}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <span className="text-[11px] font-medium text-green-700">
                                  +{bonus.bonusAmount} {sym}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEditBonus(bonus);
                                  }}
                                  className="p-0.5 rounded hover:bg-blue-100 cursor-pointer"
                                >
                                  <Pencil className="w-2.5 h-2.5 text-blue-500" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteBonus(rate.id, bonus.id);
                                  }}
                                  disabled={deleteBonusMutation.isPending}
                                  className="p-0.5 rounded hover:bg-red-100 cursor-pointer disabled:opacity-50"
                                >
                                  <Trash2 className="w-2.5 h-2.5 text-red-500" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}

                        {/* ─── Supervision Bonuses ─── */}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[10px] font-bold opacity-50 uppercase tracking-wide">
                            {t('employmentRoles.supervisionBonuses.title')}
                          </span>
                          <button
                            onClick={() => {
                              triggerHapticFeedback();
                              if (addingSupBonusForRateId === rate.id) {
                                setAddingSupBonusForRateId(null);
                                setSupBonusHospitalId('');
                                setSupBonusAmount('');
                                setSupBonusSourceRoleIds([]);
                              } else {
                                setAddingSupBonusForRateId(rate.id);
                                cancelEditSupBonus();
                              }
                            }}
                            className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold hover:bg-white/10 transition-colors cursor-pointer"
                            style={actionBtnStyle}
                          >
                            {addingSupBonusForRateId === rate.id ? <X className="w-2.5 h-2.5" /> : <Plus className="w-2.5 h-2.5" />}
                            {addingSupBonusForRateId === rate.id ? t('common.cancel') : t('employmentRoles.supervisionBonuses.add')}
                          </button>
                        </div>

                        {/* Add supervision bonus form */}
                        {addingSupBonusForRateId === rate.id && (
                          <div className="space-y-1.5 p-1.5 rounded-lg" style={{ backgroundColor: MODAL_COLORS.surface }}>
                            <div className="flex gap-1.5">
                              <select
                                value={supBonusHospitalId}
                                onChange={(e) => setSupBonusHospitalId(e.target.value)}
                                className="flex-1 p-1 rounded text-xs font-semibold outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
                                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
                              >
                                <option value="">{t('employmentRoles.supervisionBonuses.selectHospital')}</option>
                                {hospitals.map((h) => (
                                  <option key={h.id} value={h.id}>
                                    {localizedName(h, lang)}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                value={supBonusAmount}
                                onChange={(e) => setSupBonusAmount(e.target.value)}
                                min={0}
                                step={0.01}
                                placeholder={t('employmentRoles.supervisionBonuses.bonusPerUnit')}
                                className="w-20 p-1 rounded text-xs font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
                              />
                            </div>
                            <div>
                              <span className="text-[10px] font-bold opacity-50">{t('employmentRoles.supervisionBonuses.sourceRoles')}</span>
                              <div className="max-h-24 overflow-y-auto space-y-0.5 mt-0.5 p-1 rounded" style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}>
                                {(activeEmploymentRoles || []).filter(r => r.id !== employmentRole.id).map((er) => (
                                  <label key={er.id} className="flex items-center gap-1.5 text-[11px] cursor-pointer hover:bg-neutral-50 rounded px-1 py-0.5">
                                    <input
                                      type="checkbox"
                                      checked={supBonusSourceRoleIds.includes(er.id)}
                                      onChange={() => toggleSupBonusSourceRole(er.id, false)}
                                      className="neo-checkbox h-3 w-3"
                                    />
                                    <span className="font-medium">{localizedName(er, lang)}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            <button
                              onClick={() => handleAddSupBonus(rate.id)}
                              disabled={!supBonusHospitalId || !supBonusAmount || supBonusSourceRoleIds.length === 0 || createSupBonusMutation.isPending}
                              className="w-full px-2 py-1 rounded text-[10px] font-bold cursor-pointer disabled:opacity-50 text-white"
                              style={{ backgroundColor: 'var(--color-primary-500)', ...actionBtnStyle }}
                            >
                              {createSupBonusMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : t('common.add')}
                            </button>
                          </div>
                        )}

                        {/* Edit supervision bonus form */}
                        {editingSupBonus && (rate.supervisionBonuses || []).some((b) => b.id === editingSupBonus.id) && (
                          <div className="space-y-1.5 p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(254, 249, 195, 0.5)' }}>
                            <div className="flex gap-1.5">
                              <div
                                className="flex-1 p-1 rounded text-xs font-semibold"
                                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: MODAL_COLORS.surface }}
                              >
                                {editingSupBonus.hospital ? localizedName(editingSupBonus.hospital, lang) : editingSupBonus.hospitalId}
                              </div>
                              <input
                                type="number"
                                value={editingSupBonusAmount}
                                onChange={(e) => setEditingSupBonusAmount(e.target.value)}
                                min={0}
                                step={0.01}
                                placeholder={t('employmentRoles.supervisionBonuses.bonusPerUnit')}
                                className="w-20 p-1 rounded text-xs font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
                              />
                            </div>
                            <div>
                              <span className="text-[10px] font-bold opacity-50">{t('employmentRoles.supervisionBonuses.sourceRoles')}</span>
                              <div className="max-h-24 overflow-y-auto space-y-0.5 mt-0.5 p-1 rounded" style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}>
                                {(activeEmploymentRoles || []).filter(r => r.id !== employmentRole.id).map((er) => (
                                  <label key={er.id} className="flex items-center gap-1.5 text-[11px] cursor-pointer hover:bg-neutral-50 rounded px-1 py-0.5">
                                    <input
                                      type="checkbox"
                                      checked={editingSupBonusSourceRoleIds.includes(er.id)}
                                      onChange={() => toggleSupBonusSourceRole(er.id, true)}
                                      className="neo-checkbox h-3 w-3"
                                    />
                                    <span className="font-medium">{localizedName(er, lang)}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            <div className="flex gap-1.5">
                              <button
                                onClick={() => handleUpdateSupBonus(rate.id)}
                                disabled={!editingSupBonusAmount || updateSupBonusMutation.isPending}
                                className="flex-1 px-2 py-1 rounded text-[10px] font-bold cursor-pointer disabled:opacity-50 text-white"
                                style={{ backgroundColor: 'var(--color-primary-500)', ...actionBtnStyle }}
                              >
                                {updateSupBonusMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : t('common.save')}
                              </button>
                              <button
                                onClick={cancelEditSupBonus}
                                className="px-1.5 py-1 rounded text-[10px] font-bold cursor-pointer glass-surface"
                                style={actionBtnStyle}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Supervision bonuses list */}
                        {(rate.supervisionBonuses || []).length === 0 ? (
                          <p className="text-[10px] opacity-40 text-center py-1">{t('employmentRoles.supervisionBonuses.noResults')}</p>
                        ) : (
                          (rate.supervisionBonuses || []).map((sb) => (
                            <div
                              key={sb.id}
                              className="flex items-center justify-between py-0.5 px-1.5 rounded"
                              style={{ backgroundColor: MODAL_COLORS.surface }}
                            >
                              <div className="flex items-center gap-1 min-w-0 flex-1">
                                <Building2 className="w-2.5 h-2.5 opacity-40 flex-shrink-0" />
                                <div className="min-w-0">
                                  <span className="text-[11px] font-semibold truncate block">
                                    {sb.hospital ? localizedName(sb.hospital, lang) : sb.hospitalId}
                                  </span>
                                  {(sb.sourceRoles || []).length > 0 && (
                                    <div className="flex flex-wrap gap-0.5 mt-0.5">
                                      {sb.sourceRoles!.map((sr) => (
                                        <span key={sr.id} className="text-[9px] bg-amber-100 text-amber-700 rounded px-1 py-px">
                                          {sr.sourceEmploymentRole ? localizedName(sr.sourceEmploymentRole, lang) : '?'}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <span className="text-[11px] font-medium text-amber-700">
                                  +{sb.bonusPerUnit} {sym}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEditSupBonus(sb);
                                  }}
                                  className="p-0.5 rounded hover:bg-blue-100 cursor-pointer"
                                >
                                  <Pencil className="w-2.5 h-2.5 text-blue-500" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSupBonus(rate.id, sb.id);
                                  }}
                                  disabled={deleteSupBonusMutation.isPending}
                                  className="p-0.5 rounded hover:bg-red-100 cursor-pointer disabled:opacity-50"
                                >
                                  <Trash2 className="w-2.5 h-2.5 text-red-500" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Allowances */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <BadgeDollarSign className="w-4 h-4 opacity-60" />
              <span className="text-sm font-bold">{t('employmentRoles.allowances')}</span>
            </div>
            <button
              onClick={() => {
                triggerHapticFeedback();
                setShowAddAllowance(!showAddAllowance);
                cancelEditAllowance();
              }}
              className="flex items-center gap-1.5 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors cursor-pointer"
              style={actionBtnStyle}
            >
              {showAddAllowance ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
              {showAddAllowance ? t('common.cancel') : t('employmentRoles.addAllowance')}
            </button>
          </div>

          {/* Add Allowance Form */}
          {showAddAllowance && (
            <div className="space-y-2 mb-2 p-2 rounded-lg" style={{ backgroundColor: MODAL_COLORS.surface }}>
              <select
                value={allowanceTypeId}
                onChange={(e) => setAllowanceTypeId(e.target.value)}
                className="w-full p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
              >
                <option value="">{t('employmentRoles.selectAllowanceType')}</option>
                {(allowanceTypes || []).map((at) => (
                  <option key={at.id} value={at.id}>
                    {localizedName(at, lang)}
                  </option>
                ))}
              </select>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={allowanceAmount}
                  onChange={(e) => setAllowanceAmount(e.target.value)}
                  min={0}
                  step={0.01}
                  placeholder={t('employmentRoles.amount')}
                  className="flex-1 p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
                />
                <select
                  value={allowancePeriod}
                  onChange={(e) => setAllowancePeriod(e.target.value)}
                  className="flex-1 p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
                  style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
                >
                  {PERIODS.map((p) => (
                    <option key={p} value={p}>{periodLabel(p)}</option>
                  ))}
                </select>
              </div>
              <input
                type="text"
                value={allowanceNotes}
                onChange={(e) => setAllowanceNotes(e.target.value)}
                placeholder={t('employmentRoles.notes')}
                className="w-full p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
              />
              <button
                onClick={handleAddAllowance}
                disabled={!allowanceTypeId || !allowanceAmount || createAllowanceMutation.isPending}
                className="w-full px-2.5 py-1.5 rounded-lg text-xs font-bold cursor-pointer disabled:opacity-50 text-white"
                style={{ backgroundColor: 'var(--color-primary-500)', ...actionBtnStyle }}
              >
                {createAllowanceMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : t('common.add')}
              </button>
            </div>
          )}

          {/* Edit Allowance Form */}
          {editingAllowance && (
            <div className="space-y-2 mb-2 p-2 rounded-lg" style={{ backgroundColor: 'rgba(254, 249, 195, 0.5)' }}>
              <div
                className="p-1.5 rounded-lg text-sm font-semibold"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: MODAL_COLORS.surface }}
              >
                {editingAllowance.allowanceType ? localizedName(editingAllowance.allowanceType, lang) : editingAllowance.allowanceTypeId}
              </div>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={allowanceAmount}
                  onChange={(e) => setAllowanceAmount(e.target.value)}
                  min={0}
                  step={0.01}
                  placeholder={t('employmentRoles.amount')}
                  className="flex-1 p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
                />
                <select
                  value={allowancePeriod}
                  onChange={(e) => setAllowancePeriod(e.target.value)}
                  className="flex-1 p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
                  style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
                >
                  {PERIODS.map((p) => (
                    <option key={p} value={p}>{periodLabel(p)}</option>
                  ))}
                </select>
              </div>
              <input
                type="text"
                value={allowanceNotes}
                onChange={(e) => setAllowanceNotes(e.target.value)}
                placeholder={t('employmentRoles.notes')}
                className="w-full p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
              />
              <div className="flex gap-2">
                <button
                  onClick={handleUpdateAllowance}
                  disabled={!allowanceAmount || updateAllowanceMutation.isPending}
                  className="flex-1 px-2.5 py-1.5 rounded-lg text-xs font-bold cursor-pointer disabled:opacity-50 text-white"
                  style={{ backgroundColor: 'var(--color-primary-500)', ...actionBtnStyle }}
                >
                  {updateAllowanceMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : t('common.save')}
                </button>
                <button
                  onClick={cancelEditAllowance}
                  className="px-2 py-1.5 rounded-lg text-xs font-bold cursor-pointer glass-surface"
                  style={actionBtnStyle}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Allowances List */}
          {isLoadingAllowances ? (
            <div className="flex justify-center py-3">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : (allowances || []).length === 0 ? (
            <p className="text-xs opacity-50 text-center py-2">{t('employmentRoles.noAllowances')}</p>
          ) : (
            <div className="space-y-1">
              {(allowances || []).map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between py-1 px-2 rounded-lg"
                  style={{ backgroundColor: MODAL_COLORS.surface }}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <BadgeDollarSign className="w-3 h-3 opacity-40 flex-shrink-0" />
                    <div className="min-w-0">
                      <span className="text-xs font-semibold truncate block">
                        {a.allowanceType ? localizedName(a.allowanceType, lang) : a.allowanceTypeId}
                      </span>
                      {a.notes && (
                        <span className="text-[10px] opacity-40 truncate block">{a.notes}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs font-medium opacity-70">
                      {a.amount} {sym} / {periodLabel(a.period)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditAllowance(a);
                      }}
                      className="p-1 rounded hover:bg-blue-100 cursor-pointer"
                    >
                      <Pencil className="w-3 h-3 text-blue-500" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteAllowance(a.id);
                      }}
                      disabled={deleteAllowanceMutation.isPending}
                      className="p-1 rounded hover:bg-red-100 cursor-pointer disabled:opacity-50"
                    >
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Dates */}
        {role.createdAt && (
          <div className="rounded-xl p-3 space-y-1" style={cardStyle}>
            <div className="flex items-center gap-2 text-xs">
              <Calendar className="w-3.5 h-3.5 opacity-50" />
              <span className="font-bold">{t('employmentRoles.createdAt', 'Created')}:</span>
              <span className="opacity-70">{new Date(role.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Clock className="w-3.5 h-3.5 opacity-50" />
              <span className="font-bold">{t('employmentRoles.updatedAt', 'Updated')}:</span>
              <span className="opacity-70">{new Date(role.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {onEdit && (
            <button
              onClick={() => {
                triggerHapticFeedback();
                onEdit(role);
                requestClose();
              }}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-bold cursor-pointer hover:bg-white/10 transition-colors"
              style={cardStyle}
            >
              {t('employmentRoles.edit')}
            </button>
          )}
          <button
            onClick={() => {
              triggerHapticFeedback();
              openConfirm({
                title: t('employmentRoles.deleteTitle'),
                message: t('employmentRoles.deleteMessage'),
                variant: 'danger',
                confirmLabel: t('common.delete'),
                onConfirm: async () => {
                  try {
                    await deleteMutation.mutateAsync(employmentRole.id);
                    toast.success(t('employmentRoles.deleteSuccess'));
                    requestClose();
                  } catch {
                    toast.error(t('employmentRoles.deleteError'));
                  }
                },
              });
            }}
            className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl font-bold cursor-pointer"
            style={{
              backgroundColor: 'rgba(254, 226, 226, 0.6)',
              border: `1px solid ${MODAL_COLORS.border}`,
              color: '#B91C1C',
            }}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </FormModal>
  );
}
